import { type CSSProperties, memo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  GradientText,
  Layout,
  SuccessIcon,
  SupportIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useWatchedOperation } from "../hooks/useWatchedOperation";
import { amountFormatter, formatHeroAmount, formatLabeledAmount } from "../utils";
import { usablePersonName } from "../utils/homeGreeting";
import {
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
} from "../assets/icons";
import {
  hasOfacDisambiguation,
  operationDisplayStatusKey,
  operationDisplayTone,
  STATUS_TONE_COLOR,
  type OperationStatusTone,
} from "../utils/operationStatus";
import { APP_ROUTES } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


const formatReceiptDate = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatReceiptDateTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  return `${formatReceiptDate(date)}\n${date.toLocaleTimeString()}`;
};

/** Funds settle on the 5th business day after the operation is created. */
const availableReceiptDate = (isoDate: string): string => {
  const result = new Date(isoDate);
  let added = 0;
  while (added < 5) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return formatReceiptDate(result);
};

const money = (amount: number | undefined, currency: string): string =>
  formatLabeledAmount(amount ?? 0, currency);

const statusIcon = (tone: OperationStatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    case "action_required":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

/**
 * Receipt / detail screen for a single operation.
 *
 * Reached from the transactions list (TransactionsPage) by tapping a row; the
 * operation ID travels in the path (`/receipt-page/:operationId`). The layout
 * mirrors the screenbook `receipt-summary` gallery screen, populated with the
 * real operation fetched from the backend.
 */
export const ReceiptSummaryPage = memo(() => {
  const { operationId } = useParams<{ operationId: string }>();
  return (
    <RequireActiveClerkSession
      resume={
        operationId
          ? { kind: "receipt", operationId }
          : { kind: "transactions" }
      }
    >
      <ReceiptSummaryPageContent />
    </RequireActiveClerkSession>
  );
});

const ReceiptSummaryPageContent = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const { operationId } = useParams<{ operationId: string }>();

  const colorConfig = theme.whiteView;
  const { bodyCopy, dropzoneColors, footerColor } = colorConfig;

  const { operation, status } = useWatchedOperation(
    session.data?.linkId,
    operationId
  );
  const loading = status === "loading";

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("done"), {
      operation_id: operationId,
    });
  }, [operationId]);

  const renderContent = () => {
    if (loading) {
      return (
        <section className="receipt-summary-loading">
          <span
            className="loader-spinner"
            style={
              {
                "--spinner-start": bodyCopy,
                "--spinner-end": bodyCopy,
              } as CSSProperties
            }
          />
        </section>
      );
    }

    if (!operation) {
      return (
        <section className="receipt-summary-loading">
          <BodyCopy textColor={bodyCopy}>{t("receipt.notFound")}</BodyCopy>
        </section>
      );
    }

    const source = operation.sourceCurrency;
    const destination = operation.destinationCurrency;
    const needsOfac = hasOfacDisambiguation(operation);
    const statusLabel = t(operationDisplayStatusKey(operation));
    const statusTone = operationDisplayTone(operation);
    const statusColor = STATUS_TONE_COLOR[statusTone];

    const { prefix, suffix, intPart: heroInt, decPart: heroDec } = formatHeroAmount(
      operation.receiveAmount,
      destination
    );

    const senderName = usablePersonName(
      typeof session.data?.config?.senderName === "string"
        ? session.data.config.senderName
        : undefined
    );

    const summaryItems: {
      label: string;
      value: string;
      isBold?: boolean;
      statusColor?: string;
      icon?: React.ReactNode;
    }[] = [
      {
        label: t("receipt.status"),
        value: statusLabel,
        isBold: true,
        statusColor,
        icon: statusIcon(statusTone, statusColor),
      },
      { label: t("receipt.transferAmount"), value: money(operation.sendAmount, source) },
      {
        label: t("receipt.transactionFee"),
        value: money(operation.transactionFee, source),
      },
      {
        label: t("receipt.exchangeRate"),
        value: `${amountFormatter(operation.exchangeRate, destination, {
          country: "en-US",
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })} ${destination} / ${source}`,
      },
      { label: t("receipt.totalPayment"), value: money(operation.totalCost, source) },
      { label: t("receipt.receiving"), value: money(operation.receiveAmount, destination) },
      {
        label: t("receipt.fundingFee"),
        value: money(
          operation.fundingFee ?? operation.fee ?? operation.totalFee,
          source
        ),
      },
      {
        label: t("receipt.transferTaxes"),
        value: money(operation.taxFee, source),
      },
      ...(senderName
        ? [
            {
              label: t("receipt.sender"),
              value: senderName,
            },
          ]
        : []),
      {
        label: t("receipt.transactionDate"),
        value: formatReceiptDateTime(operation.createdAt),
      },
      { label: t("receipt.dateAvailable"), value: availableReceiptDate(operation.createdAt) },
    ];

    return (
      <section className="receipt-summary-container">
        <section className="summary-section">
          <section className="summary-title">
            <BodyCopy textColor={bodyCopy}>{t("receipt.youReceived")}</BodyCopy>
            <GradientText as="h1">
              {prefix}{heroInt}.<label>{heroDec}</label>
              {suffix ? (
                <span className="receipt-hero-currency">{suffix}</span>
              ) : null}
            </GradientText>
            <BodyCopy
              className="receipt-trx-caption"
              textColor={bodyCopy}
              aria-label={t("receipt.transactionId")}
            >
              {operation.operationId.slice(0, 8).toUpperCase()}
            </BodyCopy>
          </section>
          <section className="summary-info">
            {summaryItems.map(({ label, value, isBold, statusColor: color, icon }, index) => (
              <SummaryInfoItem
                key={index}
                status={label}
                value={value}
                isBold={isBold}
                statusColor={color}
                icon={icon}
              />
            ))}
          </section>
          {needsOfac && (
            <section className="footer-section" style={{ marginTop: 24 }}>
              <Button
                size="large"
                background={colorConfig.backgroundBtn}
                color={colorConfig.textColorBtn}
                onClick={() =>
                  redirectTo(
                    `${APP_ROUTES.OFAC_DISAMBIGUATION_PAGE}/${operation.operationId}`
                  )
                }
              >
                {t("receipt.completeDetails")}
              </Button>
            </section>
          )}
        </section>
        <section className="receipt-summary-disclaimers">
          <BodyCopy className="footer-text" textColor={footerColor}>
            {t("receipt.footerNote")}
          </BodyCopy>
        </section>
      </section>
    );
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="need-help-header receipt-summary-section"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content isOverflowauto className="receipt-summary-content">
          <ScrollFadeIndicator color={bodyCopy} />
          {renderContent()}
        </Layout.Content>
      </AuraLayout>
      {supportSheet}
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
